import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { BRAND, NAV } from "@/lib/site";

const scrollTo = (href) => {
  const el = document.querySelector(href);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (href) => {
    setOpen(false);
    scrollTo(href);
  };

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-zi-line shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3 sm:px-8">
        <button
          data-testid="header-logo"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-2.5"
          aria-label="Zippy Infosolution home"
        >
          <img src={BRAND.logo} alt="Zippy Infosolution" className="h-11 w-11 object-contain" />
          <span className="hidden font-heading text-lg font-bold tracking-tight text-zi-ink sm:block">
            Zippy Infosolution
          </span>
        </button>

        <nav className="hidden items-center gap-8 lg:flex">
          {NAV.map((item) => (
            <button
              key={item.href}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => handleNav(item.href)}
              className="group relative font-sans text-sm font-semibold text-zi-muted transition-colors hover:text-zi-primary"
            >
              {item.label}
              <span className="absolute -bottom-1 left-0 h-0.5 w-0 bg-zi-accent transition-all duration-300 group-hover:w-full" />
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="header-quote-button"
            onClick={() => handleNav("#contact")}
            className="hidden rounded-full bg-zi-primary px-5 py-2.5 font-heading text-sm font-semibold text-white shadow-lg shadow-zi-primary/25 transition-all duration-300 hover:bg-zi-accent sm:block"
          >
            Get a Quote
          </button>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-zi-line bg-white text-zi-ink lg:hidden"
            aria-label="Toggle menu"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden border-t border-zi-line bg-white/95 backdrop-blur-xl lg:hidden"
          >
            <div className="flex flex-col gap-1 px-5 py-4">
              {NAV.map((item) => (
                <button
                  key={item.href}
                  data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                  onClick={() => handleNav(item.href)}
                  className="rounded-lg px-3 py-3 text-left font-heading text-base font-semibold text-zi-ink hover:bg-zi-tint"
                >
                  {item.label}
                </button>
              ))}
              <button
                data-testid="mobile-quote-button"
                onClick={() => handleNav("#contact")}
                className="mt-2 rounded-full bg-zi-primary px-5 py-3 font-heading font-semibold text-white"
              >
                Get a Quote
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Header;
